import React from 'react';
import {useParams} from 'react-router-dom';
import {FeatureFlagEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkForms} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {FormsOfflineBoundary} from 'src/app/forms/FormsOfflineBoundary';
import {FormsOfflineRefreshAction} from 'src/app/forms/offline/FormsOfflineRefreshAction';
import {FormsPendingSyncBanner} from 'src/app/forms/offline/FormsPendingSyncBanner';
import {FormSubmissionsTable} from 'src/pages/Modules/Forms/shared/FormSubmissionsTable/FormSubmissionsTable';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {FormsDashViewType} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {FormDocumentTitle} from '../../../../app/forms/formHelpers';
import ListFormBuilderDefinitions from './components/ListFormBuilderDefinitions/ListFormBuilderDefinitions';
import {FormCategoryTable} from './components/FormCategoryTable/FormCategoryTable';
import {ListUserFormDefinitions} from './components/ListUserFormDefinitions/ListUserFormDefinitions';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const FormsDashPage: React.FC = () => {
  useSetDocumentTitle(FormDocumentTitle);

  const perms = useModulePermissions(ModuleEnum.Forms);
  const {isOffline} = useConnectivity();

  const navigate = useAppNavigate();
  const {view} = useParams();

  const {RenderedTabsComponent} = useUrlQueryTabV2<FormsDashViewType>({
    view: (view ?? 'user') as FormsDashViewType,
    defaultView: 'user',
    tabs: [
      {
        view: 'user',
        label: `My Forms`,
        component: <ListUserFormDefinitions />,
      },
      {
        view: 'all',
        label: `All Forms`,
        component: (
          <FormSubmissionsTable
            identifier="all-form-submissions"
            showFormDefinitionName
            titleText="All Forms"
            fromTab="all"
          />
        ),
      },
      {
        view: 'manage',
        label: `Manage Forms`,
        disabled: !perms.view || isOffline,
        component: <ListFormBuilderDefinitions />,
      },
      {
        view: 'categories',
        label: `Categories`,
        disabled: !perms.view || isOffline,
        component: <FormCategoryTable />,
      },
    ],
    onSelectView: view => {
      navigate(PathUtility.FormsDashboard(view as FormsDashViewType));
    },
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Forms}>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkForms.icon}>Forms</PageHeader.Title>
        <PageHeader.Actions>
          <FormsOfflineRefreshAction />
        </PageHeader.Actions>
      </PageHeader>
      <FormsPendingSyncBanner />
      {RenderedTabsComponent}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.FormsDashboard(':view'),
  element: (
    <FormsOfflineBoundary>
      <FormsDashPage />
    </FormsOfflineBoundary>
  ),
  isPrivate: true,
  supportsOffline: true,
});

export default FormsDashPage;
